import { logger } from '@adornis/base/logging.js';
import { LecturerData } from '@adornis/buchungsheld-db/entities/lecturerData.js';
import type { User } from '@adornis/buchungsheld-db/entities/user.js';
import type { BHTranslationDictionary } from '@adornis/buchungsheld-db/translations/translation.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import '@adornis/buchungsheld-theme/components/tr-input';
import '@adornis/buchungsheld-theme/fonts/fonts';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { fullEditorConfig } from '@adornis/html-based-buildify/client/editor/presets/full-editor.js';
import '@adornis/html-based-buildify/client/editor/x-editor.js';
import '@adornis/internationalization/client/language-picker/x-country-picker';
import '@adornis/internationalization/client/x-tel-input';
import { globalLanguageSubject } from '@adornis/translation-core/client/languageSubject.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { combineLatest, map } from 'rxjs';
import { BHFormController } from '../controller/BHFormController.js';

/**
 * @element bh-lecturer-data-form
 */
@customElement('bh-lecturer-data-form')
export class BHLecturerDataForm extends ChemistryLitElement {
  @property({ attribute: 'lecturer-id' }) lecturerID = new RXController<null | string>(this, null);
  @property({ attribute: false }) lecturerData = new RXController<null | LecturerData>(this, null);

  @state() private readonly _currUser = new RXController(this, currentUser<User>());
  @state() private readonly _translation = new TranslationController<BHTranslationDictionary>(this);
  private _isLecturerDataNewlyCreated = false;
  @state() readonly lecturerDataController = new BHFormController(
    this,
    combineLatest([this.lecturerData.observable, this.lecturerID.observable]).pipe(
      map(([lecturerData, lecturerID]) => {
        if (lecturerData) {
          this._isLecturerDataNewlyCreated = false;
          return lecturerData;
        }
        this._isLecturerDataNewlyCreated = true;
        return new LecturerData({ lecturerID });
      }),
    ),
    {
      success: async doc => {
        try {
          if (this._isLecturerDataNewlyCreated) await doc.create();
          else await doc.save();

          this._isLecturerDataNewlyCreated = false;

          void XSnackbar.show(this._translation.translate('PROFILE', 'SUCCESS_SAVING_LECTURER_DATA'));
        } catch (e) {
          logger.error({ err: e }, 'There was a problem saving lecturer data');
          return TrDialog.alert(
            'Es gab ein Problem beim Speichern der Rechnungsdaten. Bitte versuchen Sie es später erneut.',
          );
        }
      },
      disabledFields: {},
    },
  );

  override render() {
    if (!this.lecturerID.value || !this.lecturerDataController.document)
      return html` <tr-loading-state></tr-loading-state> `;
    return html`
      <tr-flex space="md">
        <tr-h4>${this._translation.translate('lecturer', 'billingInformation')}</tr-h4>

        <tr-input
          flex
          placeholder=${this._translation.translate('lecturer', 'iban')}
          type="iban"
          ${this.lecturerDataController.field('iban')}
        ></tr-input>
        <tr-flex horizontal space="md">
          <tr-input
            flex
            placeholder=${this._translation.translate('lecturer', 'salutation')}
            type="text"
            ${this.lecturerDataController.field('salutation')}
          ></tr-input>
          <tr-input
            flex
            placeholder=${this._translation.translate('lecturer', 'title')}
            type="string"
            ${this.lecturerDataController.field('title')}
          ></tr-input>
        </tr-flex>

        <x-tel-input
          placeholder=${this._translation.translate('lecturer', 'phone')}
          ${this.lecturerDataController.field('phone')}
        ></x-tel-input>

        <tr-h4>${this._translation.translate('lecturer', 'billingAddress')}</tr-h4>

        <tr-flex horizontal space="md">
          <tr-input
            flex
            placeholder=${this._translation.translate('lecturer', 'streetAndHousenumber')}
            type="text"
            ${this.lecturerDataController.field('billingAddress', 'streetAndHousenumber')}
          ></tr-input>

          <tr-input
            flex
            placeholder=${this._translation.translate('lecturer', 'additionalInfo')}
            type="text"
            ${this.lecturerDataController.field('billingAddress', 'additionalInfo')}
          ></tr-input>
        </tr-flex>

        <tr-flex horizontal space="md">
          <tr-input
            flex
            placeholder=${this._translation.translate('lecturer', 'zip')}
            type="text"
            ${this.lecturerDataController.field('billingAddress', 'zipCity', 'zip')}
          ></tr-input>

          <tr-input
            flex
            placeholder=${this._translation.translate('lecturer', 'city')}
            type="text"
            ${this.lecturerDataController.field('billingAddress', 'zipCity', 'city')}
          ></tr-input>
        </tr-flex>

        <tr-flex horizontal space="md">
          <x-country-picker
            flex
            showflag
            language=${globalLanguageSubject.getValue()}
            placeholder=${this._translation.translate('lecturer', 'country')}
            ${this.lecturerDataController.field('billingAddress', 'country')}
          >
          </x-country-picker>
        </tr-flex>

        <!-- buildify detail page -->
        ${this.renderDetailPage(this.lecturerDataController.document)}
      </tr-flex>
    `;
  }

  private renderDetailPage(lecturerData: LecturerData) {
    const hasDesign = !(lecturerData.detailPage === null);
    return html`
      <x-flex space="md">
        <br />
        <x-flex horizontal crossaxis-center space-between>
          <x-h3> ${this._translation.translate('field', 'detailPage')} </x-h3>
          <x-button
            ${css({ marginLeft: 'auto' })}
            tone=${hasDesign ? 'error' : ''}
            @click=${async () => {
              if (
                hasDesign &&
                !(await XDialog.confirm(
                  html`
                    <x-flex space="md">
                      <x-h3 ${css({ alignSelf: 'center' })}> Achtung! </x-h3>
                      <x-text>
                        Willst du dein eigen erstelltes Design wirklich löschen? Dies kann
                        <b>nicht rückgängig</b> gemacht werden
                      </x-text>
                    </x-flex>
                  `,
                  {
                    highlightDecline: true,
                    acceptText: 'Löschen',
                    declineText: 'Abbrechen',
                  },
                ))
              )
                return;
              lecturerData.detailPage = hasDesign ? null : '';
              this.requestUpdate();
            }}
          >
            ${hasDesign ? 'Design löschen' : 'Neues Design anlegen'}
          </x-button>
        </x-flex>
        ${hasDesign
          ? html`
              <x-editor
                .configs=${[...fullEditorConfig()]}
                .value=${lecturerData.detailPage}
                @value-picked=${async e => {
                  if (e.detail.value === undefined) return;
                  lecturerData.detailPage = e.detail.value;
                }}
              ></x-editor>
            `
          : html` <x-text> Kein Design angelegt. Es wird der <b>Standard</b> genutzt. </x-text> `}
      </x-flex>
    `;
  }
}
